import { getTravelText, isTravelHomeSectionHidden } from "../../../components/travelTextTokens";
import React from "react";
import Link from "next/link";
import { SectionTitle, sharedStyles } from "./_shared";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { isExternalUrl, buildLinkHref, buildMenuUrl } from "../../../components/normalizeMenuUrl";
import {isTravelHomeLinkHidden} from '../../../components/travelHomepage';

const servicesStyles = `
.tr-svc__grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
}
@media (max-width: 480px) {
  .tr-svc { flex: 1 1 calc(50% - 7px) !important; max-width: none !important; }
}

.tr-svc {
  flex: 0 1 200px;
  max-width: 240px;
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 22px 14px 18px;
  background: color-mix(in srgb, var(--primary, #1b579e) 8%, white);
  border: 1px solid color-mix(in srgb, var(--primary, #1b579e) 14%, white);
  border-radius: 18px;
  min-height: 210px;
  text-align: center;
  position: relative;
  transition: transform .2s ease, box-shadow .2s ease;
}
.tr-svc:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(0,0,0,.06);
}
.tr-svc--muted {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #6b7280;
}
.tr-svc__icon {
  width: 48px; height: 48px;
  border-radius: 14px;
  background: white;
  color: var(--primary, #1b579e);
  display: inline-flex; align-items: center; justify-content: center;
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--primary, #1b579e) 24%, white);
}
.tr-svc--muted .tr-svc__icon {
  color: #9ca3af;
  box-shadow: inset 0 0 0 1px #e5e7eb;
}
.tr-svc__status {
  font-size: 11px; font-weight: 700;
  letter-spacing: .12em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--primary, #1b579e) 55%, black);
}
.tr-svc--muted .tr-svc__status { color: #9ca3af; }
.tr-svc__name {
  font-size: 15px; font-weight: 700; color: #1a1a2e;
  line-height: 1.22;
}
.tr-svc--muted .tr-svc__name { color: #6b7280; }
.tr-svc__cta {
  margin-top: auto;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  background: white;
  color: var(--primary, #1b579e);
  font-size: 13px;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid color-mix(in srgb, var(--primary, #1b579e) 22%, white);
  transition: background .15s, color .15s;
}
.tr-svc__cta:hover {
  background: var(--primary, #1b579e);
  color: white;
}
`;

// Services : grille de cards services communautaires, autonome.
// Source : travel.services configuré depuis l'admin. Si liste vide → null.
// Chaque service est localisé via applyLang (label + ctaLabel multilingues).
export default function Services({ context }) {
    const { org, travel, lang } = context;

    const rawServices = travel?.services || [];
    const items = rawServices.map((s) => applyLang(s, lang))
        .filter(item => !isTravelHomeLinkHidden(context, item.url));

    if (items.length === 0) return null;

    const cityName = org.ville || org.name;
    const resolvedTitle = getTravelText(travel, "home_services", t`Services communautaires à ${cityName}`, lang, {city: cityName});
    const resolvedHighlight = t`Services communautaires`;

    if (isTravelHomeSectionHidden(context, "home_services")) return null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: sharedStyles + servicesStyles }} />
            <section className="tr-ui-section">
                <SectionTitle title={resolvedTitle} highlight={resolvedHighlight} />
                <div className="tr-svc__grid">
                    {items.map((it, i) => {
                        const isActive = it.active !== false;
                        const cls = `tr-svc${!isActive ? " tr-svc--muted" : ""}`;
                        // buildMenuUrl gère tous les cas (slug nu, chemin relatif, URL
                        // absolue visant cette org, lien externe) sans coller BASE_WEB_URL
                        // à la main (un slug nu produirait ".../frtravel/..." sinon).
                        const href = it.url ? buildMenuUrl(it.url, org) : null;
                        // externe = ne vise PAS le site de l'org → _blank + <a> natif.
                        const isExternal = href && isExternalUrl(href, org);

                        const ctaNode = isActive && it.ctaLabel && href && (
                            isExternal
                                ? <a href={href} target="_blank" rel="noopener noreferrer" className="tr-svc__cta">{it.ctaLabel}</a>
                                : <Link href={buildLinkHref(href, org)} legacyBehavior><a className="tr-svc__cta">{it.ctaLabel}</a></Link>
                        );

                        return (
                            <article key={i} className={cls}>
                                <div className="tr-svc__icon">
                                    {renderIcon(it.icon, 24)}
                                </div>
                                <div className="tr-svc__status">
                                    {isActive ? <Trans>Actif</Trans> : <Trans>Inactif</Trans>}
                                </div>
                                <div className="tr-svc__name">{it.label}</div>
                                {ctaNode}
                            </article>
                        );
                    })}
                </div>
            </section>
        </>
    );
}
